import { capitalize } from "@mui/material";
import { BubbleText } from "@totalenergiescode/mobility-business-rev-design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetAccountDetail } from "@/api/graphql/accounts/getAccountDocument/hooks.ts";
import { useGetNotificationsByAccount } from "@/api/graphql/notifications/hooks.ts";
import { StyledCellText } from "@/components/Agents/List";
import { NOTIFICATION_PER_PAGE } from "@/components/Notifications/constants.ts";
import {
  getCompanyName,
  getStatusAndColor,
} from "@/components/Notifications/userList/helper.ts";
import { StyledNoDataText } from "@/components/Notifications/userList/styles.ts";
import { useFormattedDate } from "@/hooks/i18n/useFormatDate";
export const useNotificationTableData = (accountId: string) => {
  const { t: tNotifications } = useTranslation("notifications");
  const formattedDate = useFormattedDate();
  const {
    data,
    isLoading: isNotificationsLoading,
    totalNotifications,
    hasNextPage,
    fetchNextPage,
  } = useGetNotificationsByAccount({
    pageSize: NOTIFICATION_PER_PAGE,
    accountId,
  });
  const { companyData, isLoading: isAccountLoading } =
    useGetAccountDetail(accountId);
  const notifications = useMemo(
    () => data?.pages?.flatMap((current) => current?.data ?? []) ?? [],
    [data],
  );
  const columns = useMemo(
    () => [
      {
        columnName: tNotifications("notifications.table.users"),
        displayedByDefault: true,
        cellWidth: "20%",
      },
      {
        columnName: tNotifications("notifications.table.object"),
        displayedByDefault: true,
        cellWidth: "45%",
      },
      {
        columnName: tNotifications("notifications.table.attachment"),
        displayedByDefault: true,
        cellWidth: "10%",
      },
      {
        columnName: tNotifications("notifications.table.status"),
        displayedByDefault: true,
        cellWidth: "15%",
      },
      {
        columnName: tNotifications("notifications.table.dateSent"),
        displayedByDefault: true,
        cellWidth: "10%",
      },
    ],
    [tNotifications],
  );
  const noDataLabel = tNotifications("notifications.dataNotAvailable");
  const rows = useMemo(
    () =>
      notifications.map((notification) => {
        const { translatedStatus, color } = getStatusAndColor({
          status: notification.status,
          failedReason: notification.failedReason,
          tNotifications,
        });
        const companyName = getCompanyName({
          code: notification.accountNumber,
          name: notification.accountName,
          tNotifications,
        });

        return {
          data: {
            account:
              companyName === noDataLabel ? (
                <StyledNoDataText variant={"text1"}>
                  {companyName}
                </StyledNoDataText>
              ) : (
                companyName
              ),
            subject: notification.subject,
            attachments: notification.hasAttachments
              ? tNotifications("notifications.attachments.with")
              : tNotifications("notifications.attachments.without"),
            status: (
              <StyledCellText variant='text2'>
                <BubbleText
                  text={capitalize(translatedStatus)}
                  variant={color}
                />
              </StyledCellText>
            ),
            dateSent: formattedDate(notification.createdTs),
          },
          key: notification.id,
        };
      }),
    [notifications, formattedDate, noDataLabel, tNotifications],
  );

  return {
    columns,
    rows,
    totalNotifications,
    isAccountLoading,
    isNotificationsLoading,
    companyData,
    hasNextPage,
    fetchNextPage,
  };
};
export const useBreadCrumbItems = (companyName?: string) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tNotifications } = useTranslation("notifications");

  return [
    {
      title: tCommon("common.features.dashboard"),
      path: "/" as const,
    },
    {
      title: companyName || "",
      path: "/account/$id" as const,
    },
    {
      title: tNotifications("notifications.title"),
      path: undefined,
    },
  ];
};
